/**
 * DividerAtom.tsx
 *
 * A generic divider/separator atom wrapping PrimeReact's Divider component
 * with design system styling and consistent API.
 */

import React from 'react';
import { Divider, DividerProps } from 'primereact/divider';
import './DividerAtom.scss';

export type DividerLayout = 'horizontal' | 'vertical';
export type DividerType = 'solid' | 'dashed' | 'dotted';
export type DividerAlign = 'left' | 'center' | 'right' | 'top' | 'bottom';
export type DividerVariant = 'default' | 'subtle' | 'strong';

export interface DividerAtomProps extends Omit<DividerProps, 'className'> {
  /** Layout direction of the divider */
  layout?: DividerLayout;
  /** Line type/style of the divider */
  type?: DividerType;
  /** Alignment of the content (if any) */
  align?: DividerAlign;
  /** Visual variant of the divider */
  variant?: DividerVariant;
  /** Additional CSS class names */
  className?: string;
  /** Content to display in the divider (text or icon) */
  children?: React.ReactNode;
}

const DividerAtom: React.FC<DividerAtomProps> = ({
  layout = 'horizontal',
  type = 'solid',
  align = 'center',
  variant = 'default',
  className = '',
  children,
  ...rest
}) => {
  // Build CSS classes
  const classes = [
    'divider-atom',
    `divider-atom--layout-${layout}`,
    `divider-atom--type-${type}`,
    `divider-atom--variant-${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    <Divider layout={layout} type={type} align={align} className={classes} {...rest}>
      {children}
    </Divider>
  );
};

DividerAtom.displayName = 'DividerAtom';

export default DividerAtom;
